import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Header from '../Header';

const StockStatus = () => {
    const [stockData, setStockData] = useState({
        company: null,
        fiscalYear: null,
        items: [],
        pagination: { current: 1, pages: 1, total: 0 }
    });
    const [displayOptions, setDisplayOptions] = useState({
        showPurchaseValue: false,
        showSalesValue: false
    });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [isSearching, setIsSearching] = useState(false);
    const [printAllLoading, setPrintAllLoading] = useState(false);
    const navigate = useNavigate();
    const searchInputRef = useRef(null);
    const searchTimeoutRef = useRef(null);

    const fetchStockStatus = useCallback(async (page = 1, search = '', limit = 10) => {
        try {
            setLoading(true);
            const params = new URLSearchParams({
                page,
                limit,
                ...(search && { search })
            });
            
            const response = await axios.get(`/api/retailer/stock-status?${params}`);
            setStockData(response.data);
            setLoading(false);
            setIsSearching(false);
        } catch (err) {
            setError(err.response?.data?.error || 'Failed to fetch stock status');
            setLoading(false);
            setIsSearching(false);
        }
    }, []);

    // Debounced search
    useEffect(() => {
        if (searchTimeoutRef.current) {
            clearTimeout(searchTimeoutRef.current);
        }
        
        searchTimeoutRef.current = setTimeout(() => {
            if (searchQuery !== '') {
                setIsSearching(true);
                fetchStockStatus(1, searchQuery);
            } else {
                fetchStockStatus(1, '');
            }
        }, 500);
        
        return () => {
            if (searchTimeoutRef.current) {
                clearTimeout(searchTimeoutRef.current);
            }
        };
    }, [searchQuery, fetchStockStatus]);

    useEffect(() => {
        fetchStockStatus(currentPage);
    }, [currentPage, fetchStockStatus]);

    const handleCheckboxChange = (e) => {
        const { name, checked } = e.target;
        setDisplayOptions(prev => ({
            ...prev,
            [name]: checked
        }));
    };

    const handlePageChange = (newPage) => {
        if (newPage >= 1 && newPage <= stockData.pagination.pages) {
            setCurrentPage(newPage);
            window.scrollTo(0, 0);
        }
    };

    // Calculate totals only for current page items
    const calculateTotals = () => {
        return stockData.items.reduce((acc, item) => {
            acc.totalStock += item.stock;
            acc.totalOpeningStock += item.openingStock;
            acc.totalQtyIn += item.totalQtyIn;
            acc.totalQtyOut += item.totalQtyOut;
            
            if (displayOptions.showPurchaseValue) {
                acc.totalPurchaseValue += item.totalStockValuePurchase;
            }
            
            if (displayOptions.showSalesValue) {
                acc.totalSalesValue += item.totalStockValueSales;
            }
            
            return acc;
        }, {
            totalStock: 0,
            totalOpeningStock: 0,
            totalQtyIn: 0,
            totalQtyOut: 0,
            totalPurchaseValue: 0,
            totalSalesValue: 0
        });
    };

    const totals = calculateTotals();

    // Fetch all items for printing
    const fetchAllItemsForPrint = async (search = '') => {
        try {
            setPrintAllLoading(true);
            const params = new URLSearchParams({
                ...(search && { search }),
                limit: 10000 // Large limit to get all items
            });
            
            const response = await axios.get(`/api/retailer/stock-status?${params}`);
            setPrintAllLoading(false);
            return response.data.items;
        } catch (err) {
            setError(err.response?.data?.error || 'Failed to fetch all items for printing');
            setPrintAllLoading(false);
            return [];
        }
    };

    // Print function for both current page and all items
    const printStockStatus = async (printAll = false) => {
        let itemsToPrint = stockData.items;
        
        if (printAll) {
            itemsToPrint = await fetchAllItemsForPrint(searchQuery);
            if (itemsToPrint.length === 0) return;
        }

        const printWindow = window.open('', '_blank');
        const printDate = new Date().toLocaleDateString();
        const fiscalYear = stockData.fiscalYear?.name || 'N/A';
        
        const printContent = `
            <!DOCTYPE html>
            <html>
            <head>
                <title>Stock Status Report</title>
                <style>
                    body { font-family: Arial, sans-serif; margin: 20px; }
                    .header { text-align: center; margin-bottom: 20px; }
                    .company-info { margin-bottom: 15px; }
                    .report-info { margin-bottom: 15px; }
                    table { width: 100%; border-collapse: collapse; margin-bottom: 20px; }
                    th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
                    th { background-color: #f2f2f2; }
                    .text-end { text-align: right; }
                    .footer { margin-top: 30px; text-align: right; }
                    @media print {
                        body { margin: 0; }
                        .no-print { display: none; }
                    }
                </style>
            </head>
            <body>
                <div class="header">
                    <h1>Stock Status Report</h1>
                </div>
                
                <div class="company-info">
                    <strong>Company:</strong> ${stockData.company?.name || 'N/A'} |
                    <strong>As on:</strong> ${printDate} |
                    <strong>F.Y:</strong> ${fiscalYear}
                </div>
                
                <div class="report-info">
                    <strong>Report:</strong> ${printAll ? 'All Items' : 'Current Page'} |
                    <strong>Total Items:</strong> ${itemsToPrint.length}
                    ${searchQuery ? `<br><strong>Search Filter:</strong> "${searchQuery}"` : ''}
                </div>
                
                <table>
                    <thead>
                        <tr>
                            <th>#</th>
                            <th>Item Name</th>
                            <th>Category</th>
                            <th>Unit</th>
                            <th class="text-end">Total Stock</th>
                            <th class="text-end">Op. Stock</th>
                            <th class="text-end">Qty. In</th>
                            <th class="text-end">Qty. Out</th>
                            <th class="text-end">Min Stock</th>
                            <th class="text-end">Max Stock</th>
                            <th class="text-end">C.P</th>
                            <th class="text-end">S.P</th>
                            ${displayOptions.showPurchaseValue ? '<th class="text-end">St.Val (C.P)</th>' : ''}
                            ${displayOptions.showSalesValue ? '<th class="text-end">St.Val (S.P)</th>' : ''}
                        </tr>
                    </thead>
                    <tbody>
                        ${itemsToPrint.map((item, index) => `
                            <tr>
                                <td>${index + 1}</td>
                                <td>${item.name}${item.code ? ` (${item.code})` : ''}</td>
                                <td>${item.category || '-'}</td>
                                <td>${item.unit || '-'}</td>
                                <td class="text-end">${item.stock.toFixed(2)}</td>
                                <td class="text-end">${item.openingStock.toFixed(2)}</td>
                                <td class="text-end">${item.totalQtyIn.toFixed(2)}</td>
                                <td class="text-end">${item.totalQtyOut.toFixed(2)}</td>
                                <td class="text-end">${item.minStock || '-'}</td>
                                <td class="text-end">${item.maxStock || '-'}</td>
                                <td class="text-end">${item.avgPuPrice.toFixed(2)}</td>
                                <td class="text-end">${item.avgPrice.toFixed(2)}</td>
                                ${displayOptions.showPurchaseValue ? `<td class="text-end">${item.totalStockValuePurchase.toFixed(2)}</td>` : ''}
                                ${displayOptions.showSalesValue ? `<td class="text-end">${item.totalStockValuePurchase.toFixed(2)}</td>` : ''}
                            </tr>
                        `).join('')}
                    </tbody>
                </table>
                
                <div class="footer">
                    <div>Printed on: ${new Date().toLocaleString()}</div>
                    <div>Printed by: ${stockData.user?.name || 'User'}</div>
                </div>
                
                <script>
                    window.onload = function() {
                        window.print();
                        setTimeout(function() {
                            window.close();
                        }, 500);
                    }
                </script>
            </body>
            </html>
        `;
        
        printWindow.document.write(printContent);
        printWindow.document.close();
    };

    return (
        <div className="container-fluid">
            <Header />
            <div className="card shadow-lg">
                <div className="card-header bg-white py-3">
                    <div className="d-flex justify-content-between align-items-center">
                        <h1 className="h3 mb-0 text-primary">
                            <i className="fas fa-boxes me-2"></i>Stock Status
                        </h1>
                        <div className="text-muted">
                            <small>As on: {new Date().toLocaleDateString()} | F.Y: {stockData.fiscalYear?.name || 'N/A'}</small>
                        </div>
                    </div>
                </div>

                <div className="card-body">
                    {/* Search and Filter Section */}
                    <div className="row mb-4 g-3">
                        <div className="col-md-8">
                            <div className="row g-3">
                                <div className="col-md-6">
                                    <div className="input-group">
                                        <span className="input-group-text">
                                            <i className="fas fa-search"></i>
                                        </span>
                                        <input
                                            type="text"
                                            className="form-control"
                                            ref={searchInputRef}
                                            placeholder="Search by item name or code..."
                                            value={searchQuery}
                                            onChange={(e) => setSearchQuery(e.target.value)}
                                            autoComplete="off"
                                        />
                                        {(searchQuery || isSearching) && (
                                            <button
                                                className="btn btn-outline-secondary"
                                                type="button"
                                                onClick={() => {
                                                    setSearchQuery('');
                                                    fetchStockStatus(1, '');
                                                }}
                                                disabled={isSearching}
                                            >
                                                {isSearching ? (
                                                    <span className="spinner-border spinner-border-sm" role="status"></span>
                                                ) : (
                                                    <i className="fas fa-times"></i>
                                                )}
                                            </button>
                                        )}
                                    </div>
                                </div>

                                <div className="col-md-6">
                                    <div className="d-flex align-items-center h-100 gap-3">
                                        <div className="form-check form-switch">
                                            <input
                                                className="form-check-input"
                                                type="checkbox"
                                                role="switch"
                                                id="showPurchaseValue"
                                                checked={displayOptions.showPurchaseValue}
                                                onChange={handleCheckboxChange}
                                                name="showPurchaseValue"
                                            />
                                            <label className="form-check-label" htmlFor="showPurchaseValue">
                                                Show Cost Price Value
                                            </label>
                                        </div>
                                        <div className="form-check form-switch">
                                            <input
                                                className="form-check-input"
                                                type="checkbox"
                                                role="switch"
                                                id="showSalesValue"
                                                checked={displayOptions.showSalesValue}
                                                onChange={handleCheckboxChange}
                                                name="showSalesValue"
                                            />
                                            <label className="form-check-label" htmlFor="showSalesValue">
                                                Show Sales Price Value
                                            </label>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Action Buttons */}
                        <div className="col-md-4">
                            <div className="d-flex justify-content-end gap-2">
                                <button
                                    className="btn btn-outline-primary"
                                    onClick={() => printStockStatus(false)}
                                    disabled={stockData.items.length === 0}
                                >
                                    <i className="fas fa-print me-2"></i>Print Current Page
                                </button>
                                <button
                                    className="btn btn-primary"
                                    onClick={() => printStockStatus(true)}
                                    disabled={stockData.pagination.total === 0 || printAllLoading}
                                >
                                    {printAllLoading ? (
                                        <>
                                            <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                                            Loading...
                                        </>
                                    ) : (
                                        <>
                                            <i className="fas fa-print me-2"></i>Print All
                                        </>
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* Pagination Controls */}
                    {stockData.pagination.pages > 1 && (
                        <div className="row mb-3">
                            <div className="col-12">
                                <nav>
                                    <ul className="pagination justify-content-center">
                                        <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                                            <button 
                                                className="page-link" 
                                                onClick={() => handlePageChange(currentPage - 1)}
                                            >
                                                Previous
                                            </button>
                                        </li>
                                        
                                        {Array.from({ length: Math.min(5, stockData.pagination.pages) }, (_, i) => {
                                            let pageNum;
                                            if (stockData.pagination.pages <= 5) {
                                                pageNum = i + 1;
                                            } else if (currentPage <= 3) {
                                                pageNum = i + 1;
                                            } else if (currentPage >= stockData.pagination.pages - 2) {
                                                pageNum = stockData.pagination.pages - 4 + i;
                                            } else {
                                                pageNum = currentPage - 2 + i;
                                            }
                                            
                                            return (
                                                <li key={pageNum} className={`page-item ${currentPage === pageNum ? 'active' : ''}`}>
                                                    <button 
                                                        className="page-link" 
                                                        onClick={() => handlePageChange(pageNum)}
                                                    >
                                                        {pageNum}
                                                    </button>
                                                </li>
                                            );
                                        })}
                                        
                                        <li className={`page-item ${currentPage === stockData.pagination.pages ? 'disabled' : ''}`}>
                                            <button 
                                                className="page-link" 
                                                onClick={() => handlePageChange(currentPage + 1)}
                                            >
                                                Next
                                            </button>
                                        </li>
                                    </ul>
                                </nav>
                                <div className="text-center text-muted small">
                                    Showing {((currentPage - 1) * 50) + 1} to {Math.min(currentPage * 50, stockData.pagination.total)} of {stockData.pagination.total} items
                                </div>
                            </div>
                        </div>
                    )}

                    {loading ? (
                        <div className="text-center py-4">
                            <div className="spinner-border text-primary" role="status">
                                <span className="visually-hidden">Loading...</span>
                            </div>
                            <p className="mt-2">Loading stock data...</p>
                        </div>
                    ) : stockData.items.length === 0 ? (
                        <div className="alert alert-info text-center py-4">
                            <i className="fas fa-info-circle me-2"></i>
                            {searchQuery ? 'No items match your search criteria' : 'No stock items found in your inventory'}
                        </div>
                    ) : (
                        <>
                            {/* Summary Cards - Updated to show current page totals */}
                            <div className="row mb-4">
                                <div className="col-md-3">
                                    <div className="card border-primary">
                                        <div className="card-body py-2">
                                            <h6 className="card-title text-muted">Items on Page</h6>
                                            <p className="card-text h4">{stockData.items.length}</p>
                                        </div>
                                    </div>
                                </div>
                                <div className="col-md-3">
                                    <div className="card border-success">
                                        <div className="card-body py-2">
                                            <h6 className="card-title text-muted">Total Stock</h6>
                                            <p className="card-text h4">{totals.totalStock.toFixed(2)}</p>
                                        </div>
                                    </div>
                                </div>
                                {displayOptions.showPurchaseValue && (
                                    <div className="col-md-3">
                                        <div className="card border-warning">
                                            <div className="card-body py-2">
                                                <h6 className="card-title text-muted">Total Value (CP)</h6>
                                                <p className="card-text h4">{totals.totalPurchaseValue.toFixed(2)}</p>
                                            </div>
                                        </div>
                                    </div>
                                )}
                                {displayOptions.showSalesValue && (
                                    <div className="col-md-3">
                                        <div className="card border-info">
                                            <div className="card-body py-2">
                                                <h6 className="card-title text-muted">Total Value (SP)</h6>
                                                <p className="card-text h4">{totals.totalSalesValue.toFixed(2)}</p>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Stock Items Table */}
                            <div className="table-responsive">
                                <table className="table table-hover table-striped">
                                    <thead className="table-light">
                                        <tr>
                                            <th width="40px">#</th>
                                            <th>Item Name</th>
                                            <th>Category</th>
                                            <th>Unit</th>
                                            <th className="text-end">Total Stock</th>
                                            <th className="text-end">Op. Stock</th>
                                            <th className="text-end">Qty. In</th>
                                            <th className="text-end">Qty. Out</th>
                                            <th className="text-end">Min Stock</th>
                                            <th className="text-end">Max Stock</th>
                                            <th className="text-end">C.P</th>
                                            <th className="text-end">S.P</th>
                                            {displayOptions.showPurchaseValue && <th className="text-end">St.Val (C.P)</th>}
                                            {displayOptions.showSalesValue && <th className="text-end">St.Val (S.P)</th>}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {stockData.items.map((item, index) => (
                                            <tr key={item._id}>
                                                <td>{((currentPage - 1) * 50) + index + 1}</td>
                                                <td>
                                                    <div className="d-flex align-items-center">
                                                        {item.stock <= (item.minStock || 0) ? (
                                                            <span className="badge bg-danger me-2">LOW</span>
                                                        ) : item.stock >= (item.maxStock || Infinity) ? (
                                                            <span className="badge bg-warning me-2">HIGH</span>
                                                        ) : null}
                                                        {item.name}
                                                        {item.code && <small className="text-muted ms-1">({item.code})</small>}
                                                    </div>
                                                </td>
                                                <td>{item.category || '-'}</td>
                                                <td>{item.unit || '-'}</td>
                                                <td className="text-end">{item.stock.toFixed(2)}</td>
                                                <td className="text-end">{item.openingStock.toFixed(2)}</td>
                                                <td className="text-end">{item.totalQtyIn.toFixed(2)}</td>
                                                <td className="text-end">{item.totalQtyOut.toFixed(2)}</td>
                                                <td className="text-end">{item.minStock || '-'}</td>
                                                <td className="text-end">{item.maxStock || '-'}</td>
                                                <td className="text-end">{item.avgPuPrice.toFixed(2)}</td>
                                                <td className="text-end">{item.avgPrice.toFixed(2)}</td>
                                                {displayOptions.showPurchaseValue && (
                                                    <td className="text-end fw-bold">{item.totalStockValuePurchase.toFixed(2)}</td>
                                                )}
                                                {displayOptions.showSalesValue && (
                                                    <td className="text-end fw-bold">{item.totalStockValueSales.toFixed(2)}</td>
                                                )}
                                            </tr>
                                        ))}
                                    </tbody>
                                    <tfoot className="table-group-divider">
                                        <tr className="fw-bold">
                                            <td colSpan="4">Page Total:</td>
                                            <td className="text-end">{totals.totalStock.toFixed(2)}</td>
                                            <td className="text-end">{totals.totalOpeningStock.toFixed(2)}</td>
                                            <td className="text-end">{totals.totalQtyIn.toFixed(2)}</td>
                                            <td className="text-end">{totals.totalQtyOut.toFixed(2)}</td>
                                            <td colSpan="2"></td>
                                            <td></td>
                                            <td></td>
                                            {displayOptions.showPurchaseValue && (
                                                <td className="text-end">{totals.totalPurchaseValue.toFixed(2)}</td>
                                            )}
                                            {displayOptions.showSalesValue && (
                                                <td className="text-end">{totals.totalSalesValue.toFixed(2)}</td>
                                            )}
                                        </tr>
                                    </tfoot>
                                </table>
                            </div>

                            {/* Bottom Pagination */}
                            {stockData.pagination.pages > 1 && (
                                <div className="row mt-3">
                                    <div className="col-12">
                                        <nav>
                                            <ul className="pagination justify-content-center">
                                                <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                                                    <button 
                                                        className="page-link" 
                                                        onClick={() => handlePageChange(currentPage - 1)}
                                                    >
                                                        Previous
                                                    </button>
                                                </li>
                                                
                                                {Array.from({ length: Math.min(5, stockData.pagination.pages) }, (_, i) => {
                                                    let pageNum;
                                                    if (stockData.pagination.pages <= 5) {
                                                        pageNum = i + 1;
                                                    } else if (currentPage <= 3) {
                                                        pageNum = i + 1;
                                                    } else if (currentPage >= stockData.pagination.pages - 2) {
                                                        pageNum = stockData.pagination.pages - 4 + i;
                                                    } else {
                                                        pageNum = currentPage - 2 + i;
                                                    }
                                                    
                                                    return (
                                                        <li key={pageNum} className={`page-item ${currentPage === pageNum ? 'active' : ''}`}>
                                                            <button 
                                                                className="page-link" 
                                                                onClick={() => handlePageChange(pageNum)}
                                                            >
                                                                {pageNum}
                                                            </button>
                                                        </li>
                                                    );
                                                })}
                                                
                                                <li className={`page-item ${currentPage === stockData.pagination.pages ? 'disabled' : ''}`}>
                                                    <button 
                                                        className="page-link" 
                                                        onClick={() => handlePageChange(currentPage + 1)}
                                                    >
                                                        Next
                                                    </button>
                                                </li>
                                            </ul>
                                        </nav>
                                    </div>
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default StockStatus;